<template>
  <Modal
    :scrollable="true"
    :name="name"
    :width="isMobile ? '100%' : 900"
    :height="isMobile ? '100%' : 680"
    class="game-search-modal"
    @opened="opened"
    @closed="closed"
  >
    <div class="game-search-modal-body">
      <div class="position-relative form-group">
        <i class="mc-search"></i>
        <input
          v-model="gameSearchText"
          type="text"
          class="w-100 form-input"
          :placeholder="$t('component.GameSearchModal.placeholder')"
        />
        <button
          type="button"
          class="position-absolute game-search-modal-close"
          @click="$modal.hide(name)"
        >
          <i class="mc-close"></i>
        </button>
      </div>
      <ul
        class="list-unstyled mb-0 game-search-modal-list js-game-search-modal-list"
      >
        <li
          v-for="(filterGameSearch, idx) in filterGameSearchList"
          :key="idx"
          :index="idx"
        >
          <Game
            :id="filterGameSearch.id"
            :key="filterGameSearch.id"
            :img-path="getGamePicPath(filterGameSearch)"
            :name="
              filterGameSearch.names[locale] || filterGameSearch.names['en']
            "
            :is-show-try="filterGameSearch.tryPlay"
            :play-params="{
              param: filterGameSearch.param,
              platform: filterGameSearch.platform,
              wallet: filterGameSearch.wallet
            }"
            :direction="'horizontal'"
            :labels="filterGameSearch.labels"
            :is-favorite="filterGameSearch.isFavorite"
          />
        </li>
      </ul>
    </div>
  </Modal>
</template>

<script>
import { checkAxiosData, compose } from 'fe-common'
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  from,
  pluck,
  switchMap,
  tap
} from 'rxjs'
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  useStore
} from '@nuxtjs/composition-api'
import { convertGame } from '../../store/game'
import { handleMessages } from '../../modules/client/utilities'
import useInfiniteScroll from '../../composables/useInfiniteScroll'
import useUser from '../../composables/useUser'
import useErrorHandler from '../../composables/useErrorHandler'
import { useSubscription } from '../../composables/observables'
import useLocale from '../../composables/useLocale'
import Game from './Game'

export default defineComponent({
  components: {
    Game
  },
  props: {
    name: {
      type: String,
      required: true,
      default: 'gameSearchModal'
    }
  },
  setup(props, context) {
    const store = useStore()

    const isMobile = computed(() => store.state.isMobile)
    const gameSearchList = computed({
      get() {
        return store.state.gameModule.gameSearchList
      },
      set(value) {
        store.commit('gameModule/GAME_SEARCH_LIST', value || [])
      }
    })
    const gameSearchText = computed({
      get() {
        return store.state.gameModule.gameSearchText
      },
      set(value) {
        store.commit('gameModule/GAME_SEARCH_TEXT', value)
      }
    })
    const infiniteScrollList = computed(() =>
      compose(filterNotSupportCurrency)(gameSearchList.value)
    )
    const {
      paginate,
      infiniteScrollPageNum,
      listeningToScroll
    } = useInfiniteScroll(infiniteScrollList)
    const filterGameSearchList = computed(() => {
      return compose(
        paginate,
        filterNotSupportCurrency
      )(gameSearchList.value)
    })
    const { locale, getGamePicPath } = useLocale()

    const { user } = useUser(context)
    const { errorHandler$ } = useErrorHandler(context)

    let isGameSearchListInit = true
    const getGameSearchText$ = context.root
      .$watchAsObservable(() => gameSearchText.value)
      .pipe(
        tap(() => (infiniteScrollPageNum.value = 1)),
        pluck('newValue'),
        debounceTime(700),
        tap(val => {
          if (!val || val.length < 2) {
            gameSearchList.value = []
            isGameSearchListInit = true
          }
        }),
        filter(val => val && val.length >= 2),
        distinctUntilChanged(
          (pre, cur) => !isGameSearchListInit && pre === cur
        ),
        switchMap(val =>
          from(
            store.dispatch('gameModule/getGames', {
              gameName: val,
              types: {
                list: ['ESPORT', 'SPORT', 'CHESSCARD', 'LOTTERY', 'ANIMAL'],
                isInclude: false
              }
            })
          )
        ),
        filter(data =>
          checkAxiosData(data, {
            handleMessages: handleMessages({
              store
            })
          })
        ),
        tap(({ data }) => {
          gameSearchList.value = (data || []).map(convertGame)
          isGameSearchListInit = false
          setTimeout(() => {
            $('.js-game-search-modal-list').scrollTop(0)
          }, 0)
        }),
        catchError(err => errorHandler$(err))
      )

    onMounted(() => {
      useSubscription(getGameSearchText$, f => f)
    })
    onBeforeUnmount(() => {
      $('.js-game-search-modal-list').off('scroll')
    })

    function opened() {
      gameSearchText.value = null
      gameSearchList.value = []
      isGameSearchListInit = true
      infiniteScrollPageNum.value = 1

      if (isMobile.value) {
        $('.js-auth-slide-modal').css('top', '0')
      }

      listeningToScroll(() => ({
        selector: '.js-game-search-modal-list',
        scrollHeight: () => $('.js-game-search-modal-list')[0].scrollHeight
      }))
    }

    function closed() {
      if (isMobile.value) {
        $('.js-auth-slide-modal').css('top', '')
      }
    }

    function filterNotSupportCurrency(list) {
      return list.filter(item =>
        user?.value &&
        item.hasOwnProperty('notSupportCurrency') &&
        item.notSupportCurrency
          ? !item.notSupportCurrency.includes(user.value.userCurrency)
          : true
      )
    }

    return {
      isMobile,
      gameSearchText,
      filterGameSearchList,
      locale,

      getGamePicPath,
      opened,
      closed
    }
  }
})
</script>
